import { mapState, mapActions, mapGetters } from 'vuex';
import {
  formatPrice,
  getStarsHTML,
  getStarsLabel,
  keysToCamel,
  swatchOptionMarkup,
  swatchDescription,
  categorySwatchOptionMarkup,
  handleize,
  htmlDecode,
  getVariantSize,
  removeSizeFromTitle,
} from 'utils/general';
import {
  addToCartEventData,
  trackItemView,
  buildItemTrackingData,
  getLocalStorageProperties,
} from 'lib/tracking';
import datalayer from 'lib/datalayer';
import bus from 'lib/bus';
import productSelector from 'mixins/product-selector';
import productBundle from 'mixins/product-bundle';
import { trackEvent } from '../../assets/js/lib/tracking';

export default {
  components: {
    modal: () => import('../modal/modal.vue'),
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
  },
  mixins: [productSelector, productBundle],
  data() {
    return {
      prevShade: false,
      currentShade: '',
      currentVariant: null,
      variant: {},
      shades: window.__GLOBAL__.shades.values,
      activeShade: {
        label: '',
        value: false,
      },
      categories: [
        {
          label: categorySwatchOptionMarkup('all', 'All Shades'),
          value: 'all',
        },
        {
          label: categorySwatchOptionMarkup('light', 'Light'),
          value: 'light',
        },
        {
          label: categorySwatchOptionMarkup('light-medium', 'Light-Medium'),
          value: 'light-medium',
        },
        {
          label: categorySwatchOptionMarkup('medium', 'Medium'),
          value: 'medium',
        },
        {
          label: categorySwatchOptionMarkup('medium-deep', 'Medium-Deep'),
          value: 'medium-deep',
        },
        {
          label: categorySwatchOptionMarkup('deep', 'Deep'),
          value: 'deep',
        },
        {
          label: categorySwatchOptionMarkup('new', 'New'),
          value: 'new',
        },
        {
          label: categorySwatchOptionMarkup(
            'limited-edition',
            'Limited Edition',
          ),
          value: 'limited-edition',
        },
      ],
      quantity: null,
      productIcons: __GLOBAL__.productIcons,
      productObject: null,
      originalBundle: null,
      bundleSecondaries: [],
      bundleVariant: null,
      bundlePrices: [],
      hideShadeDrop: true,
      OGOptIn: false,
      tooltipActive: false,
    };
  },
  beforeDestroy() {
    this.$store.commit('quickView/reset');

    if (window.installmentPaymentsManager) {
      window.installmentPaymentsManager.unregisterComponent('quick-view');
    }
  },
  async beforeMount() {
    if (
      this.product.bundled_products &&
      this.product.bundled_products.length > 0
    ) {
      this.isBundle = true;
      await this.fetchBundleProductsByHandles(this.product.bundled_products);
      this.bundleSecondaries = this.bundleProductsData.filter(
        (product, index) => index > 0,
      );
      this.bundlePrices = this.bundleProductsData.map(
        (product) => product.price,
      );

      this.productObject = this.bundleProductsData[0];
      this.variant = keysToCamel(this.product.variants[0]);
      this.bundleVariant = keysToCamel(this.productObject.variants[0]);

      this.updateCtaText(this.$t('products.product.add_to_cart_bundle'));
      this.originalBundle = this.product;
    } else {
      if (this.product.isGWP) {
        const filteredVariants = this.product.variants.filter(
          (variant) => variant.isGWPVariant,
        );
        this.productObject = { ...this.product, variants: filteredVariants };
        this.variant = this.productObject.activeVariant
          ? keysToCamel(this.productObject.activeVariant)
          : keysToCamel(filteredVariants[0]);
      } else {
        this.productObject = this.product;
        const activeVariant = this.productObject.variants.find(
          (variant) => variant.id === this.selectedVariant,
        );
        if (activeVariant) {
          this.variant = keysToCamel(activeVariant);
        } else {
          this.variant = keysToCamel(this.productObject.variants[0]);
        }
      }
    }

    if (this.updateInstallmentPayments) {
      this.updateInstallmentPayments();
    }

    if (window.installmentPaymentsManager) {
      window.installmentPaymentsManager.registerComponent(this.$el, 'quick-view');
    }

    const baseTitle = handleize(
      this.productObject.title.split(__GLOBAL__.productDelimiter)[0],
    );

    const hasMicroCollection = await this.checkMicroCollection(baseTitle);
    if (hasMicroCollection) {
      this.fetchMicroCollection({
        payload: baseTitle,
        isGWP: this.product.isGWP,
        selectedShades: this.product.selectedShades,
      });
    }

    this.currentShade = this.getShadeTitle(this.productObject);
    this.currentVariant = this.productObject;
    if (this.gwpLayout) {
      this.prevShade = {
        id:
          this.productObject.activeVariant?.id ||
          this.productObject.variants[0].id,
        title:
          this.productObject.activeVariant?.title ||
          this.productObject.variants[0].title,
      };
    }

    let payload = {
      ...this.productObject,
    };

    payload = this.addCustomOverrides(payload, this.productObject);

    this.activeShade = {
      label: swatchOptionMarkup({
        ...this.productObject,
        swatch_color_id: this.colorId,
        shade: this.getShadeTitle(this.productObject),
        hexOverride: payload?.customHexColor,
        imageOverride: payload?.customSwatchImage,
      }),
      value: this.productObject.title,
    };

    if (this.$store.state.quickView.action === 'edit') {
      const cartLine = this.$store.getters['global/cartLineByVariantId']({
        id: this.variant.id,
        isEditable: !this.product.isGWP,
      });
      if (cartLine) this.quantity = cartLine.quantity;
    } else {
      this.quantity = 1;
    }

    setTimeout(() => {
      if (typeof Afterpay !== 'undefined') {
        if (
          (this.variant.available && this.variant.id) ||
          (!this.variant.id && this.variantPrice)
        ) {
          Afterpay.createPlacements({
            targetSelector: '.quick-view__shipping-info__afterpay',
            attributes: {
              currency: 'USD',
              locale: 'en_US',
              amountSelector: this.variant.compareAtPrice
                ? '.quick-view__discount-price'
                : '.quick-view__price',
            },
          });
        }
      }
    }, 1000);
    await this.trackProductChange({
      product: this.product,
      type: 'shade',
      variant: this.variant,
    });
  },
  computed: {
    ...mapGetters('global', [
      'isOnlyOneProductGWP',
      'isGWPLayoutActive',
      'rewardProducts',
    ]),
    ...mapState('global', ['cart', 'activeCountryCode', 'origin', 'listData']),
    ...mapState('quickView', [
      'loading',
      'product',
      'microCollectionProducts',
      'activeCategory',
      'ctaText',
      'action',
      'itemToUpdate',
      'isGWP',
      'selectedVariant',
    ]),
    showFindShadeButtons() {
      return ![true, 'true'].includes(this.product.hide_shade_buttons || this.product.metafields?.general?.hide_shade_buttons);
    },
    showTryShadeOnButton() {
      if (window.__GLOBAL__.disableVto) {
        return false;
      }
      return ![true, 'true'].includes(this.product.hide_try_shade_on_button || this.product.metafields?.general?.hide_try_shade_on_button);
    },
    allSizes() {
      if (!this.product) return [];
      const sameVariants = this.microCollectionProducts?.filter((variant) => {
        const colorTag = this.product.tags.find((tag) =>
          tag.includes('Color ID'),
        );
        return colorTag && variant.tags.includes(colorTag);
      });
      if (!sameVariants) return [getVariantSize(this.currentVariant)];
      return sameVariants
        .map((variant) => getVariantSize(variant))
        .reduce((arr, current) => {
          if (arr.includes(current) || current === null) {
            return arr;
          }
          return [...arr, current];
        }, []);
    },
    microCollectionProductsShadeGrouped() {
      return this.microCollectionProducts?.reduce((arr, variant) => {
        const colorTag = variant.tags.find((tag) => tag.includes('Color ID'));

        // Filter by unique color Id tag
        if (colorTag && arr.some((elm) => elm.tags.includes(colorTag))) {
          return arr;
        }
        return [...arr, variant];
      }, []);
    },
    currentSize() {
      return getVariantSize(this.product);
    },
    variantTitle() {
      return htmlDecode(
        this.originalBundle
          ? this.originalBundle.title.split(
              window.__GLOBAL__.productDelimiter,
            )[0]
          : this.product.title.split(window.__GLOBAL__.productDelimiter)[0],
      );
    },
    variantPrice() {
      let variant = this.variant;
      if (this.originalBundle) {
        variant = this.originalBundle.variants[0];
      }
      if (!variant.price) return '';

      let price = variant.price;
      if (variant.compareAtPrice) {
        price = variant.compareAtPrice;
      }

      return formatPrice(price, true);
    },
    bundleVariantTitle() {
      return this.productObject.title.split(
        window.__GLOBAL__.productDelimiter,
      )[0];
    },
    bundleVariantPrice() {
      if (!this.bundleVariant.price) return '';

      let price = this.bundleVariant.price;
      if (this.bundleVariant.compareAtPrice) {
        price = this.bundleVariant.compareAtPrice;
      }

      return formatPrice(price, true);
    },
    variantDiscountPrice() {
      let variant = this.variant;
      if (this.originalBundle) {
        variant = this.originalBundle.variants[0];
      }
      if (!variant.price) return false;
      if (!variant.compareAtPrice) return false;
      return formatPrice(variant.price, true);
    },
    badges() {
      if (!this.product.tags) return [];

      const badges = this.product.tags
        .filter((tag) => {
          return tag.includes('badge|');
        })
        .map((badge) => {
          const text = badge.replace('badge|', '');
          return {
            class: `fenty-badge-${handleize(text)}`,
            text: text,
          };
        });

      return badges;
    },
    colorId() {
      const colorTag = this.productObject.tags.find((tag) =>
        tag.includes('Color ID'),
      );
      if (colorTag) {
        return colorTag.split('|')[1].toLowerCase();
      }
      return '';
    },
    rating() {
      if (!this.product.reviewsAverage) return '';
      return `(${this.product.reviewsAverage})`;
    },
    stars() {
      if (!this.product.reviewsAverage) return '';
      return getStarsHTML(this.product.reviewsAverage);
    },
    readReviewsLink() {
      if (!this.product.reviewsCount) return '';
      const reviewsCount = this.product.reviewsCount;
      return `View ${reviewsCount} Review${reviewsCount > 1 ? 's' : ''}`;
    },
    installmentPaymentsText() {
      const isUKStore = window.location.hostname.split(`.`).pop() === `uk` ||
                       window.location.hostname.includes('fenty-uk-staging');
      const payment_count = isUKStore ? 3 : 4;
      const text = this.$t('installment_payments.text_content');
      const price = this.variant.price;
      const installmentAmount = (price / payment_count) / 100;
      const formattedPrice = `$${Math.floor(installmentAmount * 100) / 100}`.replace('.00', '');
      return text.replace('{payment_count}', payment_count).replace('{price}', formattedPrice);
    },
    installmentPaymentsPrice() {
      return this.variant.price || 0;
    },
    customMessage() {
      return this.metafields?.general?.custom_message;
    },
    shippingMessage() {
      return this.metafields?.general?.shipping_message;
    },
    disclaimer() {
      return this.metafields?.general?.disclaimer;
    },
    icons() {
        if (this.product.icons && typeof this.product.icons === "object") {
          return this.product.icons.references.map(
            (icon) => this.productIcons[icon.index]
          );
        }

        return [];
    },
    images() {
      return this.originalBundle
        ? this.originalBundle.media.filter(
            (media) =>
              !media.type?.includes('video') &&
              !media.url?.includes('youtu.be'),
          )
        : this.product.media.filter(
            (media) =>
              !media.type?.includes('video') &&
              !media.url?.includes('youtu.be'),
          );
    },
    thumbsContainerWidth() {
      return `${0.4 * 650 * this.images.length}px`;
    },
    isSkinProduct() {
      return this.product.vendor === 'Fenty Skin';
    },
    isBeautyProduct() {
      return this.product.vendor === 'Fenty Beauty';
    },
    variantsFiltered() {
      if (
        this.microCollectionProductsShadeGrouped[0]?.optionsWithValues?.Color &&
        this.microCollectionProductsShadeGrouped[0]?.optionsWithValues?.Color[0]
      ) {
        this.hideShadeDrop =
          !this.microCollectionProductsShadeGrouped[0]?.optionsWithValues?.Color[0].startsWith(
            '#',
          );
      }
      const variantsWithColorClass =
        this.microCollectionProductsShadeGrouped.map((variant) => {
          let colorId = '';
          const colorTag = variant.tags.find((tag) => tag.includes('Color ID'));
          if (colorTag) {
            colorId = colorTag.split('|')[1].toLowerCase();
          }

          let payload = {
            ...variant,
            colorClass: `fenty-color-${colorId}`,
          };

          payload = this.addCustomOverrides(payload, variant);

          return payload;
        });

      if (this.activeCategory.value === 'all') return variantsWithColorClass;

      return variantsWithColorClass.filter((variant) => {
        const colorIdTag = variant.tags.find((t) => t.includes('Color ID'));
        const colorId = colorIdTag?.split('|')[1];
        const shade = this.shades.find((shade) => shade.id === colorId);
        if (!shade) {
          return console.warn(
            'Shade not found, ensure it is configured with correct title',
          );
        }

        if (variant.additionalCategories) {
          return (
            shade.category === this.activeCategory.value ||
            variant.additionalCategories.includes(this.activeCategory.value)
          );
        }
        return shade.category === this.activeCategory.value;
      });
    },
    starsAriaLabel() {
      if (!this.product.reviewsAverage) return '';
      return `${this.product.reviewsAverage} out of 5 stars based on ${this.product.reviewsCount} reviews`;
    },
    shadesForDropdown() {
      return this.microCollectionProductsShadeGrouped.map((variant) => {
        let colorId = '';
        const colorTag = variant.tags.find((tag) => tag.includes('Color ID'));
        if (colorTag) {
          colorId = colorTag.split('|')[1].toLowerCase();
        }

        let swatchClasses = `fenty-color-${colorId}`;
        if (!variant.available) {
          swatchClasses += ' unavailable';
        }

        let payload = {
          ...variant,
        };

        payload = this.addCustomOverrides(payload, variant);
        const shade = this.getShadeTitle(variant);

        return {
          label: swatchOptionMarkup({
            ...variant,
            shade,
            swatch_color_id: colorId,
            hexOverride: payload?.customHexColor,
            imageOverride: payload?.customSwatchImage,
          }),
          value: handleize(this.getShadeTitle(variant)),
          description: swatchDescription({
            shade,
            swatch_color_id: colorId,
          }),
        };
      });
    },
    showFilters() {
      return !['true', true].includes(
        this.productObject?.metafields?.general?.hide_shade_range_filter,
      );
    },
    buttonSize() {
      return this.gwpLayout ? 'btn--half' : 'btn--full';
    },
    gwpLayout() {
      let res = this.product.isGWP;
      if (this.isOnlyOneProductGWP) {
        res = true;
      }
      return res;
    },
    reviewsUrl() {
      return `${Shopify.routes.root}products/${this.product.handle}?section=reviews`;
    },
    isShadeProduct() {
      let isShade = true;
      if (!this.loading && this.hasShades < 1) {
        isShade = false;
      }
      return isShade;
    },
    variants() {
      return this.product.variants;
    },
  },
  methods: {
    ...mapActions('global', [
      'addRemoveProductToList',
      'openModal',
      'closeModal',
      'updateBackInStock',
    ]),
    ...mapActions('quickView', [
      'setProduct',
      'updateModalTitle',
      'updateCtaText',
      'fetchMicroCollection',
      'updateActiveCategory',
      'checkMicroCollection',
    ]),
    ...mapActions('cart', [
      'addToCart',
      'updateCartItem',
      'openCart',
      'closeCart',
      'addBundleItemsToCart',
      'updateChangePending',
    ]),
    updateInstallmentPayments() {
      this.$nextTick(() => {
        if (this.variant && this.variant.price && this.variant.price > 0) {
          if (window.installmentPaymentsManager) {
            window.installmentPaymentsManager.updateForComponent(this.$el, this.variant.price);
          }
        }
      });
    },
    addCustomOverrides(payload, variant) {
      // Check for custom hex color defined in Product metafields
      if (Boolean(variant.metafields?.swatch_management?.custom_hex)) {
        const customHexColor = variant.metafields.swatch_management.custom_hex;
        payload = {
          ...payload,
          customHexColor,
        };
      }

      // Check for custom image defined in Product metafields
      if (Boolean(variant.metafields?.swatch_management?.custom_image)) {
        const imageJSON = JSON.parse(
          variant.metafields.swatch_management.custom_image,
        );
        const { src } = imageJSON[0];
        payload = {
          ...payload,
          customSwatchImage: src,
        };
      }

      // Check for custom categories defined in Product metafields
      if (
        Boolean(variant.metafields?.swatch_management?.additional_categories)
      ) {
        const additionalCategories =
          variant.metafields.swatch_management.additional_categories
            .split('|')
            .map((category) => handleize(category));

        payload = {
          ...payload,
          additionalCategories,
        };
      }
      return payload;
    },
    getShadeTitle(variant) {
      return variant.title.includes(__GLOBAL__.productDelimiter)
        ? variant.title.split(__GLOBAL__.productDelimiter)[1].trim()
        : variant.title;
    },
    changeSizeVariant(size) {
      const sameVariantWithSize = this.microCollectionProducts?.find(
        (variant) => {
          const colorTag = variant.tags.find((tag) => tag.includes('Color ID'));
          // Find by unique color Id tag and size
          if (
            colorTag &&
            this.product.tags.includes(colorTag) &&
            getVariantSize(variant) === size
          ) {
            return true;
          }
          return false;
        },
      );
      if (sameVariantWithSize) {
        const { handle, title } = sameVariantWithSize;
        this.changeProduct(handle, removeSizeFromTitle(title), size);
      }

      // Blur the button
      setTimeout(() => {
        if ('activeElement' in document) {
          document.activeElement.blur();
        }
      }, 200);
    },
    async changeProduct(handle, variant, size = null) {
      if (handle) {
        let variantsList = this.microCollectionProducts;
        if (size && size !== null) {
          variantsList = variantsList.filter(
            (variant) => getVariantSize(variant) === size,
          );
        }
        const variantObject = variantsList.find(
          (item) => removeSizeFromTitle(item.title) === variant,
        );

        const parentId = document
          .querySelector('[data-module="bundle-builder"]')
          ?.getAttribute('data-bb-parent-id');

        const hasRestrictedSizes =
          this.product.isBbProduct && parentId
            ? variantObject.variants.find((variant) =>
                variant.bundleBuilderProductIds?.includes(parentId),
              )
            : false;

        const updatedVariants =
          this.product.isBbProduct && parentId
            ? variantObject.variants.map((variant) => {
                if (hasRestrictedSizes) {
                  if (variant.bundleBuilderProductIds?.includes(parentId)) {
                    return { ...variant, is_disabled: false };
                  } else {
                    return { ...variant, is_disabled: true };
                  }
                } else {
                  return variant;
                }
              })
            : variantObject.variants;

        this.setProduct({
          ...variantObject,
          parentTitle: this.product.parentTitle,
          listType: this.product.listType,
          cardIndex: this.product.cardIndex,
          isBundleBuilder: this.product.isBundleBuilder,
          componentName: this.product.componentName,
          origin: this.product.origin,
          siteSection: this.product.siteSection,
          isGWP: this.product.isGWP,
          isBbProduct: this.product.isBbProduct,
          replaceBBStep: this.product.replaceBBStep,
          index: this.product.index,
          listId: this.product.listId,
          listName: this.product.listName,
          variants: updatedVariants,
          hide_shade_buttons: this.product.hide_shade_buttons,
          hide_try_shade_on_button: this.product.hide_try_shade_on_button,
        });
        this.variant = updatedVariants[0];
        this.currentShade = this.getShadeTitle(variantObject);
        this.currentVariant = variantObject;
        this.productObject = this.product;
        if (this.product.isGWP || this.product.isBbProduct) {
          this.productObject.activeVariant = this.variant;
        }
        this.bundleVariant = keysToCamel(this.productObject.variants[0]);

        let payload = {
          ...this.productObject,
        };

        payload = this.addCustomOverrides(payload, this.productObject);
        const shade = this.getShadeTitle(this.productObject);

        this.activeShade = {
          label: swatchOptionMarkup({
            ...this.productObject,
            swatch_color_id: this.colorId,
            shade,
            hexOverride: payload?.customHexColor,
            imageOverride: payload?.customSwatchImage,
          }),
          value: this.productObject.title,
          description: swatchDescription({
            shade,
            swatch_color_id: this.colorId,
          }),
        };

        this.$nextTick(() => {
          if (this.updateInstallmentPayments && typeof this.updateInstallmentPayments === 'function') {
            this.updateInstallmentPayments();
          }
        });

        await this.trackProductChange({
          product: this.productObject,
          type: 'shade',
          variant: this.variant,
        });
      }
    },
    handle(string) {
      return handleize(string);
    },
    onCategoryChange(category) {
      this.updateActiveCategory(category);
    },
    onSwatchChange(handle, variant) {
      this.changeProduct(handle, variant);
    },
    onShadeChange(shade) {
      const variant = this.microCollectionProducts.find(
        (item) =>
          handleize(this.getShadeTitle(item)) ===
          handleize(shade.value).replace(/[?!]/g, ''),
      );
      if (variant) {
        this.updateActiveCategory(this.categories[0]);
        this.changeProduct(variant.handle, variant.title);
      }
    },
    async changeSimpleVariant(variant) {
      this.variant = variant;
      if (this.updateInstallmentPayments) {
        this.updateInstallmentPayments();
      }

      await this.trackProductChange({
        product: this.product,
        type: 'simpleVariant',
        variant,
      });
    },
    async trackProductChange(data) {
      const { product } = data;

      const otherData = {
        variant: this.variant,
        ...(product.componentName && {
          componentName: product.componentName,
        }),
        origin: product.origin,
        originProduct: htmlDecode(this.variantTitle),
      };

      trackItemView(product, otherData, product.origin || 'Quick Shop');
    },

    updateQuantity(quantity) {
      this.quantity = quantity;
    },
    getAddToCartType() {
      let addToCartType = 'Home';
      if (window.location.pathname.indexOf('/collections') > -1) {
        addToCartType = 'Product List Page';
        if (window.location.pathname.indexOf('/products') > -1) {
          addToCartType = 'Product Detail Page';
        }
      } else if (window.location.pathname.indexOf('/cart') > -1) {
        addToCartType = 'Cart';
      } else if (window.location.pathname.indexOf('/products') > -1) {
        addToCartType = 'Product Detail Page';
      } else if (window.location.pathname.indexOf('/pages') > -1) {
        addToCartType = 'Landing Page';
      } else if (window.location.pathname.indexOf('/account') > -1) {
        addToCartType = 'Account Page';
      }

      return addToCartType;
    },
    // Add To Bundle Builder
    addProductToBundle() {
      this.product.activeVariant = this.variant;
      if (typeof this.product.replaceBBStep !== 'undefined') {
        bus.$emit('bb-delete-all', this.product.replaceBBStep);
        bus.$emit('bb-item-added', this.product);
      } else {
        bus.$emit('bb-item-added', this.product);
      }
      setTimeout(() => {
        this.closeModal();
      }, 500);
    },
    addProduct() {
      const callback = () => {
        setTimeout(() => {
          this.closeCart();
          this.updateChangePending(false);
        }, 2500);
      };

      this.updateChangePending(true);

      const extraData = !this.isBundle
        ? {
            addToCartType: this.getAddToCartType(),
            sku:
              typeof this.product?.sku !== 'undefined'
                ? this.product?.sku
                : this.product?.id,
          }
        : false;

      const promoText =
        this.product.metafields?.cart_promo?.promo_text ||
        this.product.promo_text;

      let properties = {
        _product_collection: this.product.categories[0],
        _product_tags: this.productObject.tags,
        _product_rating:
          (this.productObject.reviewsAverage) ?? null,
        _upsell_bundle: this.product.cart_upsell_bundle,
        _promo_text: promoText,
        _promo_link: this.product.promo_link,
      };

      if (this.gwpLayout) {
        if (this.isOnlyOneProductGWP) {
          this.updateItemQuantity({
            id: this.variant.id,
            quantity: 1,
            properties: { _gwp: true },
          });
        } else {
          let prev;
          let filteredVariants;

          if (this.microCollectionProducts.length) {
            filteredVariants = this.microCollectionProducts
              .flatMap((product) =>
                product.variants.filter((variant) => variant.isGWPVariant),
              )
              .filter(Boolean);
          } else {
            filteredVariants = this.product.variants.filter(
              (variant) => variant.isGWPVariant,
            );
          }

          const itemInCart = this.cart.items.find((item) =>
            filteredVariants?.some((variant) => +variant.id === +item.id),
          );

          prev = itemInCart
            ? { id: itemInCart.id, title: itemInCart.title }
            : this.prevShade;

          const otherData = {
            reviews_average:
              this.product.reviewsAverage || '',
          };

          this.addRemoveProductToList({
            id: this.variant.id,
            tags: this.product.tags,
            prev,
            otherData,
          });
        }
      } else {
        const eventName = this.quantity === 0 ? 'removeFromCart' : 'addToCart';
        properties = {
          ...properties,
          _product_collection: this.product.categories[0],
          _product_tags: this.product.tags,
          _product_rating:
            this.productObject.reviewsAverage,
          ...getLocalStorageProperties(),
        };
        if (this.action === 'edit') {
          if (this.itemToUpdate !== this.variant.id) {
            const removeCallback = (subscription) => {
              setTimeout(() => {
                this.addToCart({
                  variant: this.variant,
                  quantity: this.quantity,
                  callback,
                  subscription,
                  properties,
                  moduleName: 'quick view'
                });
                datalayer.dataLayerPush(
                  eventName,
                  addToCartEventData(this.product, this.quantity, extraData),
                );
              }, 2000);
            };
            this.updateCartItem({
              variantId: this.itemToUpdate,
              quantity: 0,
              callback: removeCallback,
              isEditable: true,
            });
          } else {
            this.updateCartItem({
              variantId: this.variant.id,
              quantity: this.quantity,
              callback,
            });
          }
        } else {
          if (this.isBundle) {
            this.addBundleToCart({
              price: this.originalBundle.variants[0].price,
              title: this.originalBundle.title,
              productObject: this.originalBundle,
              quantity: this.quantity,
              callback,
              properties,
            });
          } else {
            const sameIdProducts = this.cart.items.filter(
              (item) => Number(item.id) === Number(this.variant.id),
            );
            const sameIdProductsQuantity = sameIdProducts.reduce(
              (acc, rec) => acc + rec.quantity,
              0,
            );
            const quantity = 5 - sameIdProductsQuantity;
            const addToCartQuantity = Math.min(this.quantity, quantity);

            if (!sameIdProductsQuantity || sameIdProductsQuantity < 5) {
              this.addToCart({
                variant: this.variant,
                quantity: addToCartQuantity,
                callback,
                properties,
                moduleName: 'quick view'
              });

              datalayer.dataLayerPush(
                eventName,
                addToCartEventData(this.product, this.quantity, extraData),
              );
            }
          }
        }
      }
      this.cancelQuickView();
    },
    redirectToProduct() {
      window.location = `${Shopify.routes.root}products/${this.product.handle}`;
    },
    openNotifyModal() {
      this.updateBackInStock({ product: this.product, variant: this.variant });
      this.openModal('back-in-stock');
    },
    cancelQuickView() {
      setTimeout(() => {
        this.closeModal();
      }, 1000);
    },
    bundleItemPrice(variant) {
      if (!variant.price) return '';

      let price = variant.price;
      if (variant.compareAtPrice) {
        price = variant.compareAtPrice;
      }

      return formatPrice(price, true);
    },
    goToShadeFinder() {
      location.href = __GLOBAL__.shades.finderHandle;
    },
    goToPDP() {
      location.href = `${Shopify.routes.root}products/${this.productObject.handle}?try-shade=true`;
    },
    formatPriceMethod(price) {
      return formatPrice(price);
    },
    buildShadeClassObject(variant) {
      return {
        [variant.colorClass]: !Boolean(variant?.customSwatchImage),
        active: this.currentVariant.id === variant.id,
        unavailable: !variant.available,
        new: variant?.additionalCategories?.includes('new'),
      };
    },
    buildInlineStyleObject({ customSwatchImage, customHexColor }) {
      if (!Boolean(customSwatchImage) && !Boolean(customHexColor)) return;

      let styleObject = {};
      if (Boolean(customHexColor)) {
        styleObject = {
          backgroundColor: `${customHexColor} !important`,
        };
      }

      if (Boolean(customSwatchImage)) {
        styleObject = {
          backgroundColor: 'unset !important',
          backgroundImage: `url('${customSwatchImage}')`,
        };
      }

      return styleObject;
    },
    showTooltipContent() {
      this.tooltipActive = true;
    },
    hideTooltipContent() {
      this.tooltipActive = false;
    },
    checkFocus(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.hideTooltipContent();
      }
    }
  },
};
